import { useState, useEffect, useRef } from 'react';
import { T } from '../styles/token.js';
import { useTheme } from '../context/ThemeContext';
import { api, handleAuthFailure } from '../data/api';
import Badge from '../components/Badge';
import Toggle from '../components/Toggle';
import Btn from '../components/Btn';
import Input from '../components/Input';
import { TableWrap, TH, TD } from '../components/TableWrap';
import { MODEL_FAMILIES } from '../data/deviceModels';

// Mirrors server/routes/ota.js. The server derives the bundle's shape from the
// file itself and re-validates on upload — this only drives the form.
const BUNDLE_EXTS = ['.tar.gz', '.tgz', '.tar', '.zip', '.apk'];
const IMAGE_EXTS  = ['.tar.gz', '.tgz', '.tar'];

/** The box's three independently-versioned images (see ota-updater.sh). */
const OTA_IMAGES = ['srt-av', 'srt-webagent', 'srt-ipout'];

const hasKnownExt   = (name) => BUNDLE_EXTS.some(e => String(name || '').toLowerCase().endsWith(e));
const isImageBundle = (name) => IMAGE_EXTS.some(e => String(name || '').toLowerCase().endsWith(e));

export default function OTAManagement({ readOnly = false, canDelete = true }) {
  const { mode } = useTheme();

  const [affiliates, setAffiliates] = useState([]);
  const [channels, setChannels]     = useState([]);
  const [devices, setDevices]       = useState([]);
  const [updates, setUpdates]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]             = useState(null);
  const [success, setSuccess] = useState(null);

  const [tab, setTab]               = useState("all");
  const [version, setVersion]       = useState("");
  const [description, setDescription] = useState("");
  const [apkFile, setApkFile]       = useState(null);
  const [targetModel, setTargetModel] = useState("");   // "" = any model
  const [targetImage, setTargetImage] = useState("");   // image bundles only
  const [forceUpdate, setForceUpdate] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [pushing, setPushing]       = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef                = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [affs, chs, devs, otaList] = await Promise.all([
        api.affiliates.list().catch(() => []),
        api.channels.list().catch(() => []),
        api.devices.list({}).catch(() => []),
        api.ota.list(),
      ]);
      setAffiliates(affs.filter(a => a.status !== 'archived'));
      setChannels(chs);
      setDevices(devs);
      setUpdates(otaList);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError("Failed to load OTA data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleTarget = (id) =>
    setSelectedTargets(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const tabItems = tab === "all"
    ? []
    : tab === "affiliate"
    ? affiliates.map(a => ({ id: a.id,  label: a.org_name,     sub: a.id }))
    : tab === "channel"
    ? channels.map(c =>   ({ id: c.id,  label: c.display_name, sub: c.id }))
    : devices.map(d =>    ({ id: d.id,  label: d.id,           sub: d.device_type === 'self-srt-connect' ? '' : (d.device_model || '—') }));

  const acceptFile = (file) => {
    setApkFile(file);
    // Auto-fill version from filename, e.g. "eBox-3.2.2.apk" → "3.2.2"
    const match = file.name.match(/([\d]+\.[\d]+(?:\.[\d]+)?)/);
    if (match && !version.trim()) setVersion(match[1]);
    // …and the image, e.g. "srt-webagent-1.1.tar.gz" → "srt-webagent". The
    // packaging step in SELF-CONTAINED-AND-OTA.md produces exactly this name.
    if (isImageBundle(file.name)) {
      const guess = OTA_IMAGES.find(n => file.name.toLowerCase().startsWith(n));
      if (guess) setTargetImage(guess);
    } else {
      setTargetImage("");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    acceptFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    // Was `.apk` only, which silently ignored a dropped image tarball.
    if (file && hasKnownExt(file.name)) acceptFile(file);
  };

  const handleDelete = async (u) => {
    setDeleteTarget(null);
    setError(null);
    setSuccess(null);
    setDeletingId(u.id);
    try {
      await api.ota.delete(u.id);
      setSuccess(`Deleted OTA update v${u.version}.`);
      await load();
    } catch (e) {
      setError(e.message || "Failed to delete update");
    } finally {
      setDeletingId(null);
    }
  };

  const bundleIsImage = isImageBundle(apkFile?.name);

  const handlePush = async () => {
    if (!apkFile)             { setError("Please select a bundle file"); return; }
    if (!version.trim())      { setError("Version is required"); return; }
    if (!description.trim())  { setError("Description is required"); return; }
    if (bundleIsImage && !targetImage) {
      setError("An image bundle must say which image it replaces — pick one under Image.");
      return;
    }
    if (tab !== "all" && selectedTargets.length === 0) {
      setError(`Select at least one ${tab}, or switch to the "All boxes" tab. A ${tab}-scoped update with no targets reaches no device.`);
      return;
    }
    setError(null);
    setSuccess(null);
    setPushing(true);
    try {
      await api.ota.create({
        file:         apkFile,
        version:      version.trim(),
        description:  description.trim(),
        target_level: tab,
        target_model: targetModel,
        target_image: targetImage,
        force_update: forceUpdate,
        target_ids:   selectedTargets,
      });
      setSuccess(
        `${bundleIsImage ? `${targetImage} image` : "Script bundle"} v${version.trim()} registered for ` +
        `${tab === "all" ? "all boxes" : `${selectedTargets.length} ${tab}(s)`}` +
        `${targetModel ? ` running ${targetModel}` : ""}.`
      );
      setVersion("");
      setDescription("");
      setApkFile(null);
      setTargetModel("");
      setTargetImage("");
      setSelectedTargets([]);
      setForceUpdate(false);
      await load();
    } catch (e) {
      setError(e.message || "Failed to push update");
    } finally {
      setPushing(false);
    }
  };

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingBottom:14, borderBottom:`1px solid ${T.border}` }}>
        <h1 style={{ fontSize:17, fontWeight:700, color:T.ink }}>OTA Management</h1>
      </div>

      {error && (
        <div style={{ background:"var(--red-bg,#fef2f2)", border:"1px solid var(--red-border,#fca5a5)", borderRadius:7, padding:"10px 14px", fontSize:12, color:"var(--red,#dc2626)", marginBottom:14 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background:"var(--green-bg,#f0fdf4)", border:"1px solid var(--green-border,#86efac)", borderRadius:7, padding:"10px 14px", fontSize:12, color:"var(--green,#16a34a)", marginBottom:14 }}>
          {success}
        </div>
      )}
      {loading ? (
        <div style={{ fontSize:12, color:T.textDim, padding:"40px 0", textAlign:"center" }}>Loading…</div>
      ) : (
        <>
          {!readOnly && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>

            {/* Upload */}
            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.textDim, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14 }}>Upload Bundle</div>
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                style={{ border:`1.5px dashed ${apkFile ? T.ink : T.border}`, borderRadius:7, padding:"28px 20px", textAlign:"center", marginBottom:14, cursor:"pointer", background: apkFile ? T.surfaceAlt : T.surfaceAlt, transition:"border-color 0.15s" }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk,.zip,.tar,.tar.gz,.tgz"
                  style={{ display:"none" }}
                  onChange={handleFileChange}
                />
                <div style={{ width:32, height:32, borderRadius:"50%", background:T.surface, border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 8px" }}>
                  <span style={{ fontSize:14, color: apkFile ? T.ink : T.textDim }}>{apkFile ? "✓" : "↑"}</span>
                </div>
                {apkFile ? (
                  <>
                    <div style={{ fontSize:12, color:T.ink, fontWeight:600 }}>{apkFile.name}</div>
                    <div style={{ fontSize:11, color:T.textDim, marginTop:3 }}>
                      {bundleIsImage ? "Image bundle — docker load on the box" : "Script bundle — unpacked over the filesystem"}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize:12, color:T.textSub, fontWeight:500 }}>Drop bundle here or browse</div>
                    <div style={{ fontSize:11, color:T.textDim, marginTop:3 }}>Max 2 GB · .zip / .apk (script) · .tar.gz / .tgz / .tar (image)</div>
                  </>
                )}
              </div>
              <Input label="Version" value={version} onChange={e => setVersion(e.target.value)} placeholder="e.g. 3.2.2" style={{ marginBottom:14 }} />
              {bundleIsImage && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:"var(--text-sub)", marginBottom:5 }}>
                    Image <span style={{ color:"var(--red,#dc2626)" }}>*</span>
                  </div>
                  <select
                    value={targetImage}
                    onChange={e => setTargetImage(e.target.value)}
                    style={{
                      width:"100%", boxSizing:"border-box",
                      background:"var(--surface)", border:"1px solid var(--border)",
                      borderRadius:6, padding:"8px 10px",
                      fontSize:12, color:"var(--text)", fontFamily:"inherit",
                      outline:"none",
                    }}
                  >
                    <option value="">Select an image…</option>
                    {OTA_IMAGES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <div style={{ fontSize:11, color:"var(--text-dim)", marginTop:5, lineHeight:1.5 }}>
                    Which container this tarball replaces. Each image is versioned separately on the box, so this version number only competes with other {targetImage || "image"} bundles.
                  </div>
                </div>
              )}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"var(--text-sub)", marginBottom:5 }}>Hardware model</div>
                <select
                  value={targetModel}
                  onChange={e => setTargetModel(e.target.value)}
                  style={{
                    width:"100%", boxSizing:"border-box",
                    background:"var(--surface)", border:"1px solid var(--border)",
                    borderRadius:6, padding:"8px 10px",
                    fontSize:12, color:"var(--text)", fontFamily:"inherit",
                    outline:"none",
                  }}
                >
                  <option value="">Any model</option>
                  {MODEL_FAMILIES.map(m => <option key={m} value={m}>{m} only</option>)}
                </select>
                <div style={{ fontSize:11, color:"var(--text-dim)", marginTop:5, lineHeight:1.5 }}>
                  {targetModel
                    ? `Only ${targetModel} boxes will be offered this bundle. Version numbers are per-model, so the same version may also exist for another model.`
                    : "Offered to every model. Pick a model when the bundle only applies to one — the two boards take different bundle formats."}
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"var(--text-sub)", marginBottom:5 }}>
                  Description <span style={{ color:"var(--red,#dc2626)" }}>*</span>
                </div>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Description"
                  rows={3}
                  style={{
                    width:"100%", boxSizing:"border-box",
                    background:"var(--surface)", border:"1px solid var(--border)",
                    borderRadius:6, padding:"8px 10px",
                    fontSize:12, color:"var(--text)", fontFamily:"inherit",
                    resize:"vertical", outline:"none",
                  }}
                />
              </div>
              <button
                onClick={handlePush}
                disabled={pushing}
                style={{ width:"100%", background:T.ink, border:"none", borderRadius:6, padding:"9px", color: T.bg, cursor: pushing ? "not-allowed" : "pointer", fontSize:12, fontWeight:600, fontFamily:"inherit", opacity: pushing ? 0.7 : 1 }}
              >
                {pushing ? "Pushing…" : "Push Update"}
              </button>
            </div>

            <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.textDim, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14 }}>Target Selection</div>

              {/* Tab buttons */}
              <div style={{ display:"flex", gap:4, marginBottom:14 }}>
                {["all","affiliate","channel","device"].map(t2 => (
                  <button key={t2} onClick={() => { setTab(t2); setSelectedTargets([]); }} style={{ background: tab===t2 ? T.ink : T.surface, border:`1px solid ${tab===t2 ? T.ink : T.border}`, borderRadius:5, padding:"5px 12px", color: tab===t2 ? T.bg : T.textSub, cursor:"pointer", fontSize:11, fontWeight: tab===t2 ? 600 : 400, textTransform:"capitalize", fontFamily:"inherit" }}>
                    {t2 === "all" ? "All boxes" : `By ${t2}`}
                  </button>
                ))}
              </div>

              {/* Item list */}
              <div style={{ display:"flex", flexDirection:"column", gap:2, maxHeight:200, overflowY:"auto", marginBottom:14 }}>
                {tab === "all" ? (
                  <div style={{ fontSize:12, color:T.textSub, padding:"12px 0" }}>This update will be offered to <strong>every active box</strong> running an older version. No target selection needed.</div>
                ) : tabItems.length === 0 ? (
                  <div style={{ fontSize:12, color:T.textDim, padding:"12px 0" }}>No {tab}s found.</div>
                ) : tabItems.map(item => {
                  const sel = selectedTargets.includes(item.id);
                  return (
                    <label key={item.id} onClick={() => toggleTarget(item.id)} style={{ display:"flex", alignItems:"center", gap:9, padding:"7px 8px", borderRadius:5, cursor:"pointer", background: sel ? T.surfaceAlt : "transparent", transition:"background 0.1s" }}>
                      <div style={{ width:14, height:14, borderRadius:3, border:`1.5px solid ${sel ? T.ink : T.border}`, background: sel ? T.ink : "transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {sel && <span style={{ color: T.bg, fontSize:9, fontWeight:900 }}>✓</span>}
                      </div>
                      <div>
                        <span style={{ fontSize:12, fontFamily:"'IBM Plex Mono', monospace", color:T.ink }}>{item.label}</span>
                        {item.sub !== item.label && (
                          <span style={{ fontSize:11, color:T.textSub, marginLeft:6 }}>{item.sub}</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Selected count */}
              {selectedTargets.length > 0 && (
                <div style={{ fontSize:11, color:T.textSub, marginBottom:10 }}>
                  {selectedTargets.length} selected
                  <button onClick={() => setSelectedTargets([])} style={{ background:"none", border:"none", cursor:"pointer", color:T.textDim, fontSize:11, marginLeft:8, fontFamily:"inherit" }}>Clear</button>
                </div>
              )}

              {/* Force update toggle */}
              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:12, display:"flex", alignItems:"center", gap:8 }}>
                <Toggle on={forceUpdate} onChange={setForceUpdate} />
                <span style={{ fontSize:12, color:T.textSub }}>Force Update</span>
              </div>
            </div>
          </div>
          )}

          {/* Update history */}
          <div style={{ fontSize:11, fontWeight:700, color:T.textDim, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Update History</div>
          <TableWrap>
            <thead>
              <tr>
                <TH>Version</TH>
                <TH>Description</TH>
                <TH>Uploaded</TH>
                <TH>Bundle</TH>
                <TH>Model</TH>
                <TH>Target Level</TH>
                <TH>Targets</TH>
                <TH>Status</TH>
                <TH></TH>
              </tr>
            </thead>
            <tbody>
              {updates.length === 0 ? (
                <tr><td colSpan={9} style={{ padding:"24px", textAlign:"center", fontSize:12, color:T.textDim }}>No updates yet.</td></tr>
              ) : updates.map((u, i) => (
                <tr key={u.id || i}>
                  <TD mono style={{ fontWeight:600 }}>{u.version}</TD>
                  <TD>
                    <span style={{ fontSize:12, color:"var(--text-sub)" }} title={u.description || ""}>
                      {u.description
                        ? u.description.length > 60
                          ? u.description.slice(0, 60) + "…"
                          : u.description
                        : "—"}
                    </span>
                  </TD>
                  <TD sub>{u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</TD>
                  <TD sub>{(u.bundle_type || 'script') === 'image' ? (u.target_image || 'image') : 'script'}</TD>
                  <TD sub>{u.target_model || 'Any'}</TD>
                  <TD sub style={{ textTransform:"capitalize" }}>{u.target_level}</TD>
                  <TD mono sub>{u.targets || '—'}</TD>
                  <TD><Badge status={u.status} /></TD>
                  <TD>
                    {canDelete && (
                      <button
                        onClick={() => setDeleteTarget(u)}
                        disabled={deletingId === u.id}
                        title="Delete OTA update"
                        style={{
                          background:"var(--red-bg, #fef2f2)",
                          border:"1px solid var(--red-border, #fecaca)",
                          borderRadius:6,
                          padding:"5px 10px",
                          fontSize:12,
                          color:"var(--red, #dc2626)",
                          cursor: deletingId === u.id ? "not-allowed" : "pointer",
                          opacity: deletingId === u.id ? 0.5 : 1,
                          fontFamily:"inherit",
                        }}
                      >
                        {deletingId === u.id ? "Deleting…" : "Delete"}
                      </button>
                    )}
                  </TD>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "28px", width: 380, maxWidth: "90vw", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", fontFamily: "inherit" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>Delete OTA Update?</div>
            <div style={{ fontSize: 13, color: "var(--text-sub)", marginBottom: 24, lineHeight: 1.6 }}>
              Are you sure you want to delete <strong style={{ color: "var(--text)" }}>v{deleteTarget.version}</strong>? The DB record and uploaded file will be removed. This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => handleDelete(deleteTarget)} style={{ flex: 1, background: "var(--red, #dc2626)", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
              <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: "9px 0", fontSize: 13, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
